import clsx from "clsx";
import dayjs from "dayjs";
import { Download } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { useGetDocumentDownloadUrl } from "~/api/default/default";
import { Document } from "~/api/model";
import { Button } from "~/components/ui/button";
import { TableCell, TableRow } from "~/components/ui/table";
import { UserAvatar } from "~/components/ui/user-avatar";
import { DocumentImageName } from "~/features/case/components/list/DocumentImage";
import { formatBytes, useOrganizationParams } from "~/helpers";

type DocumentsListItemProps = {
  document: Document;
};

export const ArchivedDocumentsListItem: React.FC<DocumentsListItemProps> = ({
  document,
}) => {
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: getDocumentDownloadUrl } = useGetDocumentDownloadUrl();

  const onDocumentDownloaded = async () => {
    const { url } = await getDocumentDownloadUrl({
      organizationId,
      projectId: document.folder?.id ?? "",
      documentId: document.id,
      params: {
        update_counter: true,
        archived: true,
      },
    });
    window.open(url, "_blank");
  };

  return (
    <>
      <TableRow
        key={document.id}
        className={clsx(
          "text-zinc-500 !text-sm !font-medium leading-tight w-full grid grid-cols-6 sm:grid-cols-12 gap-2 sm:gap-4",
          "group"
        )}
        data-document-id={document.id}
      >
        <TableCell className="py-4 font-medium col-span-3 sm:col-span-4 text-black">
          <DocumentImageName document={document} />
        </TableCell>
        <TableCell className="py-4 hidden sm:flex items-center col-span-2">
          <span className="text-foreground text-sm font-normal leading-tight truncate">
            {document.folder?.name}
          </span>
        </TableCell>
        <TableCell className="py-4 hidden sm:flex items-center col-span-1">
          <span className="text-zinc-500 text-sm font-normal leading-tight">
            {formatBytes(Number(document.uploadSize) || Number(document.size))}
          </span>
        </TableCell>
        <TableCell className="py-4 flex items-center col-span-2">
          <span className="text-zinc-500 text-sm font-normal leading-tight">
            {dayjs(document.archivedAt).format("DD/MM/YYYY")}
          </span>
        </TableCell>
        <TableCell className="py-4 hidden sm:flex items-center col-span-2">
          {document.archivedBy && (
            <div className="flex items-center gap-2">
              <UserAvatar name={document.archivedBy.name} size="sm" />
              <span className="text-sm font-medium">
                {document.archivedBy.name}
              </span>
            </div>
          )}
        </TableCell>
        <TableCell className="py-4 flex items-center justify-end col-span-1">
          <Button
            variant="ghost"
            className="hover:bg-transparent sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
            onClick={onDocumentDownloaded}
          >
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">
              <FormattedMessage id="download" defaultMessage="Download" />
            </span>
          </Button>
        </TableCell>
      </TableRow>
    </>
  );
};
